import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import stripePromise from '../stripePromise';
import { useSubscription } from '../useSubscription';
import { useAuth } from '../AuthContext';
import Paywall from '../Paywall';
import AuthModal from '../components/AuthModal';
import CheckoutModal from '../components/CheckoutModal';
import './PricingPage.css';


const PLAN_META = {
  'price_1TKOZvILOpB6RcrOkie5R5Kb': { label: 'Yearly plan',  price: '$24.99 / year'  },
  'price_1TKORnILOpB6RcrOkFwJKC0e': { label: 'Monthly plan', price: '$3.99 / month' },
};

const FREE_FEATURES = [
  { icon: '📊', label: 'Frequency Analysis',    desc: 'Full draw-count bar chart for every number' },
  { icon: '🔥', label: 'Hot & Cold Tracker',    desc: 'See which numbers are running hot or ice cold' },
  { icon: '📋', label: 'Draw History',          desc: 'Paginated history of every past draw' },
  { icon: '🗺',  label: 'Heatmap',              desc: 'Color-intensity grid across all numbers' },
  { icon: '🎯', label: 'Number Picker (random)', desc: 'Quick-pick random numbers instantly' },
  { icon: '📅', label: 'Date Range Filters',    desc: 'Filter by 1, 3, or 5-year windows' },
  { icon: '⚡', label: 'Powerball & Mega Millions', desc: 'Full data for both games' },
];

const PRO_FEATURES = [
  { icon: '📈', label: 'Trends Analysis',    desc: 'Cumulative frequency curves over 2,800+ draws' },
  { icon: '🔔', label: 'Distribution & Stats', desc: 'Histogram with mean, median, std dev & skewness' },
  { icon: '🔗', label: 'Number Pairs',       desc: 'Co-occurrence analysis — which numbers run together' },
  { icon: '⏱',  label: 'Gap Analysis',       desc: 'Track the most overdue numbers by draw gap' },
  { icon: '🎯', label: 'Smart Picker Modes', desc: 'Weighted-by-frequency & most-overdue strategies' },
];

export default function PricingPage() {
  const navigate                 = useNavigate();
  const { session }              = useAuth();
  const { isPro, refetch }       = useSubscription();
  const [showPaywall,    setShowPaywall]    = useState(false);
  const [showAuthModal,  setShowAuthModal]  = useState(false);
  const [checkout,       setCheckout]       = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleUpgradeClick = () => {
    if (!session) {
      setShowAuthModal(true);
    } else {
      setShowPaywall(true);
    }
  };

  const handleSelectPlan = useCallback(async (priceId, promoResult = null) => {
    if (!session) return;
    setShowPaywall(false);
    setCheckoutLoading(true);
    try {
      const res  = await fetch('/api/create-subscription.php', {
        method:  'POST',
        headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ price_id: priceId }),
      });
      const data = await res.json();
      if (data.clientSecret) {
        setCheckout({
          clientSecret:    data.clientSecret,
          priceId,
          promotionCodeId: promoResult?.promotionCodeId ?? null,
          promoDiscount:   promoResult ? { name: promoResult.name, percentOff: promoResult.percentOff, amountOff: promoResult.amountOff } : null,
        });
      }
    } catch (err) {
      console.error('[checkout] error:', err);
    } finally {
      setCheckoutLoading(false);
    }
  }, [session]);

  const handleCheckoutSuccess = useCallback(async () => {
    setCheckout(null);
    await refetch();
    navigate('/app');
  }, [refetch, navigate]);

  return (
    <div className="pricing-page">

      <nav className="pricing-nav">
        <Link to="/" className="pricing-nav-logo">LottoPickle</Link>
        <div className="pricing-nav-right">
          <Link to="/app" className="pricing-nav-link">Open App</Link>
          <Link to="/" className="pricing-nav-link">Home</Link>
        </div>
      </nav>

      <div className="pricing-hero">
        <span className="pricing-tag">Pricing</span>
        <h1 className="pricing-title">Simple, honest pricing</h1>
        <p className="pricing-sub">Start free. Upgrade when you want deeper analysis.</p>
      </div>

      <div className="pricing-cards">

        {/* Free */}
        <div className="pricing-card pricing-card-free">
          <div className="pricing-card-header">
            <span className="pricing-plan-name">Free</span>
            <div className="pricing-price">
              <span className="pricing-amount">$0</span>
              <span className="pricing-period">forever</span>
            </div>
            <Link to="/app" className="pricing-cta-btn pricing-cta-free">Start for free</Link>
          </div>
          <ul className="pricing-feature-list">
            {FREE_FEATURES.map((f) => (
              <li key={f.label} className="pricing-feature-item">
                <span className="pricing-feat-icon">{f.icon}</span>
                <span>
                  <strong>{f.label}</strong>
                  <span className="pricing-feat-desc"> — {f.desc}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Pro */}
        <div className="pricing-card pricing-card-pro">
          <div className="pricing-card-header">
            <div className="pricing-pro-badge">Most popular</div>
            <span className="pricing-plan-name">Premium</span>
            <div className="pricing-price-group">
              <div className="pricing-price">
                <span className="pricing-amount">$3.99</span>
                <span className="pricing-period">/ month</span>
              </div>
              <div className="pricing-price pricing-price-annual">
                <span className="pricing-amount-annual">$24.99</span>
                <span className="pricing-period">/ year</span>
                <span className="pricing-savings">Save 48%</span>
              </div>
            </div>
            {isPro ? (
              <div className="pricing-cta-btn pricing-cta-current">You're on Premium ⭐</div>
            ) : (
              <button
                className="pricing-cta-btn pricing-cta-pro"
                onClick={handleUpgradeClick}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? 'Loading…' : 'Upgrade to Premium'}
              </button>
            )}
          </div>
          <p className="pricing-pro-includes">Everything in Free, plus:</p>
          <ul className="pricing-feature-list">
            {PRO_FEATURES.map((f) => (
              <li key={f.label} className="pricing-feature-item">
                <span className="pricing-feat-icon">{f.icon}</span>
                <span>
                  <strong>{f.label}</strong>
                  <span className="pricing-feat-desc"> — {f.desc}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="pricing-fine-print">Auto-renews until cancelled. Cancel any time.</p>
        </div>

      </div>

      {/* FAQ */}
      <div className="pricing-faq">
        <h2 className="pricing-faq-title">Common questions</h2>
        <div className="pricing-faq-grid">
          {[
            { q: 'Can I try before I pay?',         a: 'Yes — the free tier is fully functional with no time limit. No credit card required.' },
            { q: 'What data does this use?',         a: 'All data comes from the New York State government open data portal (data.ny.gov), updated after every draw.' },
            { q: 'Does past frequency predict future draws?', a: 'No. Lottery draws are mathematically independent events. This is entertainment and data analysis, not gambling advice.' },
            { q: 'How do I cancel?',                a: 'Cancel any time from your account settings. You keep Premium access until the end of the billing period.' },
          ].map((item) => (
            <div className="pricing-faq-item" key={item.q}>
              <h3 className="pricing-faq-q">{item.q}</h3>
              <p className="pricing-faq-a">{item.a}</p>
            </div>
          ))}
        </div>
      </div>

      <footer className="pricing-footer">
        <div className="pricing-footer-links">
          <Link to="/">Home</Link>
          <Link to="/app">App</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/refunds">Refunds</Link>
          <a href="mailto:info@lottopickle.com">Contact</a>
        </div>
        <p className="pricing-footer-disclaimer">For entertainment and data analysis purposes only. Not gambling advice.</p>
      </footer>

      {showPaywall && (
        <Paywall onSelectPlan={handleSelectPlan} onClose={() => setShowPaywall(false)} />
      )}

      {showAuthModal && (
        <AuthModal
          reason="Create a free account to subscribe"
          onSuccess={async (newSession) => {
            setShowAuthModal(false);
            if (newSession?.access_token) {
              const res  = await fetch('/api/subscription-status.php', {
                headers: { Authorization: 'Bearer ' + newSession.access_token },
              });
              const data = await res.json();
              if (!data.isPro) setShowPaywall(true);
              refetch();
            }
          }}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      {checkout && (
        <CheckoutModal
          stripePromise={stripePromise}
          clientSecret={checkout.clientSecret}
          priceId={checkout.priceId}
          accessToken={session?.access_token}
          promotionCodeId={checkout.promotionCodeId}
          promoDiscount={checkout.promoDiscount}
          planLabel={PLAN_META[checkout.priceId]?.label ?? 'Premium'}
          planPrice={PLAN_META[checkout.priceId]?.price ?? ''}
          onSuccess={handleCheckoutSuccess}
          onClose={() => setCheckout(null)}
        />
      )}

    </div>
  );
}
